import React, { useState, useContext, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import moduleConfig from "./moduleConfig";
import { Store as toast } from "react-notifications-component";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import axios from "../../../config/axios";
import { toastNotification } from "../../../config/toastNotification";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import { ContainerLoading } from "../../UI/loading/ContainerLoading";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import { isEmpty } from "../../../helpers/CommonHelper";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSRichTextEditor from "../../UI/forms/CMSRichTextEditor";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import CMSFormDate from "../../UI/forms/CMSFormDate";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormFile from "../../UI/forms/CMSFormFile";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import {
  updateInitialState,
  updateReducer,
} from "../../../reducers/module/updateReducer";
import {
  loadItemGeneral,
  onSubmitGeneral,
} from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";

const SpotLightAreaUpdate = () => {
  const [updateState, updateDispatch] = useReducer(
    updateReducer,
    updateInitialState
  );
  const [formInitialValues, setFormInitialValues] = useState({});
  const { setBreadcrumb } = useContext(BreadcrumbContext);

  let { id } = useParams();
  let navigate = useNavigate();

  const loadItem = async () => {
    await loadItemGeneral(id, `${moduleConfig.url}/${id}`, updateDispatch);
  };
  useEffect(() => {
    loadItem();
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      // moduleConfig.breadcrumbs.details,
      moduleConfig.breadcrumbs.update,
    ]);
  }, [id]);

  useEffect(() => {
    if (updateState.data.item && updateState.data.item._id) {
      setFormInitialValues(updateState.data.item);
    }
  }, [updateState.data]);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {updateState.status === "fetching" && updateState.isLoading && (
        <ContainerLoading />
      )}
      {updateState.error && updateState.status === "fetchFailed" ? (
        <BlockErrorMessage error={updateState.error} />
      ) : (
        <>
          <ModuleHeader
            moduleTitle={`Update ${moduleConfig.singleItemTitle}`}
            moduleUrl={moduleConfig.url}
          ></ModuleHeader>

          {Object.keys(formInitialValues).length > 0 &&
            updateState.data &&
            updateState.data.item &&
            !isEmpty(updateState.data.item._id) && (
              <section className="content">
                <Card>
                  <Card.Body>
                    {updateState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        ...formInitialValues,
                        file: updateState.data.item.image,
                      }}
                      validationSchema={yup.object({
                        position: yup
                          .string()
                          .required("Position is required."),

                        slideBackgroundColor: yup
                          .string()
                          .required("Slide background color is required."),

                        heading1: yup
                          .string()
                          .required("Heading 1 is required.")
                          .max(10, "Heading 1 cannot exceed 10 characters."),

                        heading1Color: yup
                          .string()
                          .required("Heading 1 color is required."),

                        heading2: yup
                          .string()
                          .required("Heading 2 is required.")
                          .max(10, "Heading 2 cannot exceed 10 characters."),

                        heading2Color: yup.string().when("heading2", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema.required("Heading 2 color is required."),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonText: yup.string(),

                        buttonTextColor: yup.string().when("buttonText", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema.required("Button text color is required."),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonLink: yup.string().when("buttonText", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema
                              .required("Button link is required.")
                              .url("Please enter a valid URL."),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonBackgroundColor: yup.string().when("buttonText", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema.required(
                              "Button background color is required."
                            ),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonIcon: yup.string(),

                        imagePosition: yup
                          .string()
                          .required("Image position is required."),

                        imageSize: yup
                          .string()
                          .required("Image size is required."),

                        file: yup.mixed().when("position", {
                          is: (pos) => pos !== "Center", // if NOT center → file REQUIRED
                          then: (schema) =>
                            schema
                              .required("Please upload an image.")
                              .imageTypeCheck(
                                moduleConfig.imageRequirement.allowedExtensions
                              )
                              .imageSizeCheck(
                                moduleConfig.imageRequirement.size
                              ),
                          otherwise: (schema) => schema.notRequired(), // if center → file NOT required
                        }),

                        sortOrder: yup
                          .number()
                          .typeError("Sort order must be a number.")
                          .required("Sort order is required."),

                        status: yup.string().required("Status is required."),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        if (values.position === "Center") {
                          delete values.file;
                        }
                        const formData = new FormData();
                        for (let name in values) {
                          if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          `${moduleConfig.url}/${id}`,
                          moduleConfig.url,
                          updateDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {/* {console.log(form.errors)} */}
                          {updateState.error && (
                            <FormErrorMessage error={updateState.error} />
                          )}

                          <CMSFormSelect
                            name="position"
                            label="Position"
                            required={true}
                            form={form}
                            options={[
                              { label: "Left", value: "Left" },
                              { label: "Center", value: "Center" },
                              { label: "Right", value: "Right" },
                            ]}
                          />

                          <CMSFormColor
                            name="slideBackgroundColor"
                            label="Background Color"
                            required={true}
                            form={form}
                          />

                          <CMSFormInput
                            name="heading1"
                            label="Heading 1"
                            required={true}
                            form={form}
                          />

                          <CMSFormColor
                            name="heading1Color"
                            label="Heading 1 Color"
                            required={true}
                            form={form}
                          />

                          <CMSFormInput
                            name="heading2"
                            label="Heading 2"
                            required={true}
                            form={form}
                          />

                          <CMSFormColor
                            name="heading2Color"
                            label="Heading 2 Color"
                            required={form.values.heading2 ? true : false}
                            form={form}
                          />

                          <CMSFormInput
                            name="buttonText"
                            label="Button Text"
                            required={false}
                            form={form}
                          />

                          <CMSFormColor
                            name="buttonTextColor"
                            label="Button Text Color"
                            required={form.values.buttonText ? true : false}
                            form={form}
                          />

                          <CMSFormInput
                            name="buttonLink"
                            label="Button Link"
                            required={form.values.buttonText ? true : false}
                            form={form}
                          />

                          <CMSFormColor
                            name="buttonBackgroundColor"
                            label="Button Color"
                            required={form.values.buttonText ? true : false}
                            form={form}
                          />

                          <CMSFormSelect
                            name="buttonIcon"
                            label="Button Icon"
                            required={false}
                            form={form}
                            options={[
                              { label: "Right Arrow", value: "Right Arrow" },
                              { label: "Plus", value: "Plus" },
                            ]}
                          />

                          <CMSFormImageWithCrop
                            label="Image"
                            name="file"
                            info="Recommended dimensions 500*500"
                            form={form}
                            aspect={1 / 1}
                            outputSize={imageRequirement.dimension}
                            required={
                                form.values.position != "Center" ? true : false
                              }
                          />

                          <CMSFormSelect
                            name="imagePosition"
                         label={
                              form.values.position != "Center"
                                ? "Image Position"
                                : "Product Image Position"
                            }
                            required={true}
                            form={form}
                            options={[
                              { label: "Right", value: "Right" },
                              { label: "Left", value: "Left" },
                            ]}
                          />

                          <CMSFormSelect
                            name="imageSize"
                              label={
                              form.values.position != "Center"
                                ? "Image Size"
                                : "Product Image Size"
                            }
                            required={true}
                            form={form}
                            options={[
                              { label: "Small", value: "Small" },
                              { label: "Large", value: "Large" },
                            ]}
                          />

                          <CMSFormInput
                            name="sortOrder"
                            label="Sort Order"
                            form={form}
                            required={true}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />

                          <button className="btn btn-primary" type="submit">
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            )}
        </>
      )}
    </DashboardLayout>
  );
};
export default SpotLightAreaUpdate;
